import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { useNotifications } from '@/hooks/use-notifications';
import useCreateContributorMutation from '@/state/mutations/contributors/use-create-contributor.ts';
import { ContributorAutocomplete, type ActorOption } from '../../common/contributor-autocomplete';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import useAddFlowContributor from '@/state/mutations/flows/add-flow-contributor';
import useCreateContributorRoleMutation from '@/state/mutations/contributors/use-create-contributor-role.ts';
import { useCreateContributorFlowRoleSlotByEventSlotMutation } from '@/state/mutations/contributors/use-create-contributor-flow-role-slot-by-event-slot';
import type { ContributorDto, ContributorRole, RoleDto } from '@/types/dtos/contributors/response/contributor.dto';
import { EVENT_CREW_ROLES, FLOW_CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { eventHasPermission, managePermissionForEventCrewRoleName } from '@/utils/event-crew-permissions';
import {
  contributorHasActiveEventCrewOnEvent,
  getAcceptedEventIdsForRoleMatch,
} from '@/utils/contributor-extraction.utils';
import { useQueryClient } from '@tanstack/react-query';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { settleContributorAddVisibility } from '@/helpers/settle-contributor-add-visibility.helper';
import { ContributorBusyNameField } from '../../common/contributor-busy-name-field';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

const matchesFlowEntity = (entity: unknown, flowEntityId: string): boolean => {
  const id = typeof entity === 'string' ? entity : normalizeId(entity);
  const origin =
    typeof entity === 'object' && entity ? normalizeId((entity as { origin?: unknown }).origin) : undefined;
  return id === flowEntityId || origin === flowEntityId;
};

const contributorHasRoleOnEntity = (
  c: ContributorDto,
  entityType: 'Event' | 'Flow',
  entityId: string,
  roleName: string,
): boolean => {
  return c.roles.some((r) => {
    if ((r as { deleted?: boolean }).deleted) return false;
    if (r.entityType !== entityType || (r.role as string) !== roleName) return false;
    if (entityType === 'Event') {
      return normalizeId(r.entity) === entityId;
    }
    return matchesFlowEntity(r.entity, entityId);
  });
};

type ContributorChoice = { kind: 'existing'; actor: ActorOption } | { kind: 'new'; name: string };

type AddCrewContributorInputProps = {
  eventSlotId?: string;
  onSuccess: () => void;
} & (
  | { variant: 'event'; eventId: string }
  | { variant: 'flow'; flow: FlowDto; flowEntityId: string; eventId: string; multiFlow: boolean | undefined }
);

function targetEntityForRole(
  props: AddCrewContributorInputProps,
  role: string,
): { type: 'Event' | 'Flow'; id: string } {
  if (props.variant === 'event') {
    return { type: 'Event', id: props.eventId };
  }
  const isEventRole = (EVENT_CREW_ROLES as readonly string[]).includes(role);
  if (isEventRole) {
    return { type: 'Event', id: props.eventId };
  }
  return { type: 'Flow', id: props.flowEntityId };
}

export const AddCrewContributorInput = (props: AddCrewContributorInputProps) => {
  const { eventSlotId, onSuccess } = props;
  const t = useTranslations();
  const notification = useNotifications();
  const contributors = useEventStore((s) => s.contributors);
  const event = useEventStore((s) => s.event);
  const eventIdFromStore = event?.origin ? getObjectId(event.origin) : event?.id;
  const acceptedEventIds = useMemo(() => getAcceptedEventIdsForRoleMatch(event), [event]);
  const { data: permissions } = usePermissionsQuery();
  const eventPerms = eventIdFromStore ? permissions?.events?.[eventIdFromStore] : undefined;

  const [contributorChoice, setContributorChoice] = useState<ContributorChoice | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [inviteeEmail, setInviteeEmail] = useState('');

  const { mutateAsync: addFlowContributorMutation, isPending: isAddContributorPending } = useAddFlowContributor();
  const { mutateAsync: createContributorMutation, isPending: isCreateContributorPending } =
    useCreateContributorMutation();
  const { mutateAsync: createContributorRoleMutation, isPending: isCreateContributorRolePending } =
    useCreateContributorRoleMutation();
  const { mutateAsync: createFlowRoleSlotByEventSlot, isPending: isCreateRoleSlotPending } =
    useCreateContributorFlowRoleSlotByEventSlotMutation();
  const queryClient = useQueryClient();
  const [isSettling, setIsSettling] = useState(false);

  const settleAfterAdd = async () => {
    const extraQueryKeys: (readonly unknown[])[] = [];
    if (props.variant === 'flow') {
      extraQueryKeys.push([GET_FLOW_QUERY_KEY, props.flow.id]);
      const flowOriginId = props.flow.origin ? getObjectId(props.flow.origin) : undefined;
      if (flowOriginId && flowOriginId !== props.flow.id) {
        extraQueryKeys.push([GET_FLOW_QUERY_KEY, flowOriginId]);
      }
    }
    await settleContributorAddVisibility(queryClient, {
      eventId: props.eventId,
      extraQueryKeys,
    });
  };

  const selectableRoles = useMemo(() => {
    const canAssignEventRole = (role: string) =>
      eventHasPermission(eventPerms, managePermissionForEventCrewRoleName(role));

    if (props.variant === 'event') {
      return [...EVENT_CREW_ROLES].filter((r) => canAssignEventRole(r));
    }
    const flowRoles = [...FLOW_CREW_ROLES] as string[];
    if (props.multiFlow === false) {
      return [...EVENT_CREW_ROLES.filter((r) => canAssignEventRole(r)), ...flowRoles];
    }
    return flowRoles;
  }, [props, eventPerms]);

  const targetEntity = useMemo(() => {
    if (!selectedRole) {
      if (props.variant === 'event') {
        return { type: 'Event' as const, id: props.eventId };
      }
      return { type: 'Flow' as const, id: props.flowEntityId };
    }
    return targetEntityForRole(props, selectedRole);
  }, [props, selectedRole]);

  const needsEventCrewEmail = useMemo(() => {
    if (!selectedRole || targetEntity.type !== 'Event') return false;
    return (EVENT_CREW_ROLES as readonly string[]).includes(selectedRole);
  }, [selectedRole, targetEntity.type]);

  useEffect(() => {
    setSelectedRole('');
    setInviteeEmail('');
  }, [contributorChoice]);

  useEffect(() => {
    if (!selectedRole) {
      setInviteeEmail('');
      return;
    }
    if (!(EVENT_CREW_ROLES as readonly string[]).includes(selectedRole)) {
      setInviteeEmail('');
      return;
    }
    if (contributorChoice?.kind === 'existing' && contributors) {
      const c = contributors.find((x) => x.id === contributorChoice.actor.id);
      setInviteeEmail(c?.email?.trim() || c?.invitedEmail?.trim() || '');
    } else if (contributorChoice?.kind === 'new') {
      setInviteeEmail('');
    }
  }, [selectedRole, contributorChoice, contributors]);

  const allContributorActors = useMemo<ActorOption[]>(() => {
    if (!contributors?.length) return [];
    return contributors.map((contributor) => ({
      id: contributor.id,
      name: contributor.profiles[0]?.displayName || `Contributor ${contributor.id.slice(-6)}`,
    }));
  }, [contributors]);

  const rolesStillAssignable = useMemo(() => {
    if (!contributorChoice || contributorChoice.kind === 'new') {
      return selectableRoles;
    }
    const c = contributors?.find((x) => x.id === contributorChoice.actor.id);
    if (!c) return selectableRoles;
    return selectableRoles.filter((role) => {
      const { type, id } = targetEntityForRole(props, role);
      if (
        type === 'Event' &&
        (EVENT_CREW_ROLES as readonly string[]).includes(role) &&
        contributorHasActiveEventCrewOnEvent(c, acceptedEventIds)
      ) {
        return false;
      }
      return !contributorHasRoleOnEntity(c, type, id, role);
    });
  }, [contributorChoice, contributors, selectableRoles, props, acceptedEventIds]);

  const resolveEventCrewInviteeEmail = (existingContributorId?: string) => {
    const fromField = inviteeEmail.trim();
    if (fromField) return fromField;
    if (!existingContributorId || !contributors) return '';
    const c = contributors.find((x) => x.id === existingContributorId);
    return (c?.email?.trim() || c?.invitedEmail?.trim() || '') ?? '';
  };

  const addExisting = async (actor: ActorOption): Promise<boolean> => {
    if (!contributors || !selectedRole) return false;
    try {
      if (targetEntity.type === 'Flow' && props.variant === 'flow') {
        await addFlowContributorMutation({
          flowId: props.flow.id,
          flowVersion: props.flow.version,
          contributorId: actor.id,
        });
        await createContributorRoleMutation({
          contributorId: actor.id,
          role: {
            entityType: 'Flow',
            entity: props.flowEntityId,
            role: selectedRole as ContributorRole,
          },
          eventSlotId,
        });
      } else {
        const resolvedEmail = resolveEventCrewInviteeEmail(actor.id);
        if (needsEventCrewEmail && !resolvedEmail) {
          notification.notifyError(t('event.detail.crewWidget.inviteeEmailRequired'));
          return false;
        }
        await createContributorRoleMutation({
          contributorId: actor.id,
          role: {
            entityType: 'Event',
            entity: props.eventId,
            role: selectedRole as ContributorRole,
            ...(needsEventCrewEmail && resolvedEmail ? { inviteeEmail: resolvedEmail } : {}),
          },
          eventSlotId,
        });
      }

      notification.notifySuccess(t('event.detail.crewWidget.addSuccess', { name: actor.name }));
      await settleAfterAdd();
      onSuccess();
      return true;
    } catch (error) {
      console.error(error);
      notification.notifyError(t('event.detail.crewWidget.addError'));
      return false;
    }
  };

  const createNew = async (name: string): Promise<boolean> => {
    if (!selectedRole) return false;
    try {
      const eventIdForCreate =
        props.variant === 'event' ? props.eventId : props.variant === 'flow' ? props.eventId : undefined;
      if (!eventIdForCreate) {
        throw new Error('Missing event id');
      }

      if (targetEntity.type === 'Flow' && props.variant === 'flow') {
        const newContributor: ContributorDto = await createContributorMutation({
          event: eventIdForCreate,
          profiles: [{ displayName: name, editor: 'Event' }],
          roles: [
            {
              entityType: 'Flow',
              entity: props.flowEntityId,
              role: selectedRole as ContributorRole,
            },
          ],
        });

        const crewRole = newContributor.roles?.find((role) => {
          const r = role as RoleDto<ContributorRole> & { deleted?: boolean };
          return (
            r.entityType === 'Flow' &&
            matchesFlowEntity(r.entity, props.flowEntityId) &&
            (r.role as string) === selectedRole &&
            !r.deleted
          );
        });

        let roleId: string;
        if (!crewRole) {
          const updated = await createContributorRoleMutation({
            contributorId: newContributor.id,
            role: {
              entityType: 'Flow',
              entity: props.flowEntityId,
              role: selectedRole as ContributorRole,
            },
            eventSlotId,
          });
          const created = updated.roles?.find((role) => {
            const r = role as RoleDto<ContributorRole> & { deleted?: boolean };
            return (
              r.entityType === 'Flow' &&
              matchesFlowEntity(r.entity, props.flowEntityId) &&
              (r.role as string) === selectedRole &&
              !r.deleted
            );
          });
          if (!created) throw new Error('Failed to create flow crew role');
          roleId = created.id;
        } else {
          roleId = crewRole.id;
        }

        if (eventSlotId) {
          try {
            await createFlowRoleSlotByEventSlot({
              contributorId: newContributor.id,
              roleId,
              eventSlotId,
            });
          } catch (error: unknown) {
            const errorMessage =
              (error &&
              typeof error === 'object' &&
              'response' in error &&
              error.response &&
              typeof error.response === 'object' &&
              'data' in error.response &&
              error.response.data &&
              typeof error.response.data === 'object' &&
              'message' in error.response.data
                ? String(error.response.data.message)
                : null) ||
              (error && typeof error === 'object' && 'message' in error ? String(error.message) : '') ||
              '';
            const responseStatus =
              error &&
              typeof error === 'object' &&
              'response' in error &&
              error.response &&
              typeof error.response === 'object' &&
              'status' in error.response &&
              typeof error.response.status === 'number'
                ? error.response.status
                : undefined;
            const isAlreadyExistsError =
              responseStatus === 400 &&
              (errorMessage.includes('already') ||
                errorMessage.includes('exists') ||
                errorMessage.includes('duplicate'));
            if (!isAlreadyExistsError) throw error;
          }
        }
      } else {
        const resolvedEmail = inviteeEmail.trim();
        if (needsEventCrewEmail && !resolvedEmail) {
          notification.notifyError(t('event.detail.crewWidget.inviteeEmailRequired'));
          return false;
        }
        await createContributorMutation({
          event: eventIdForCreate,
          ...(needsEventCrewEmail && resolvedEmail ? { email: resolvedEmail.toLowerCase() } : {}),
          profiles: [{ displayName: name, editor: 'Event' }],
          roles: [
            {
              entityType: 'Event',
              entity: props.eventId,
              role: selectedRole as ContributorRole,
            },
          ],
        });
      }

      notification.notifySuccess(t('event.detail.crewWidget.addSuccess', { name }));
      await settleAfterAdd();
      onSuccess();
      return true;
    } catch (error) {
      console.error(error);
      notification.notifyError(t('event.detail.crewWidget.addError'));
      return false;
    }
  };

  const canSubmitAdd = useMemo(() => {
    if (!contributorChoice || !selectedRole) return false;
    if (rolesStillAssignable.length === 0) return false;
    if (!rolesStillAssignable.includes(selectedRole)) return false;
    if (!needsEventCrewEmail) return true;
    if (contributorChoice.kind === 'existing') {
      const fromField = inviteeEmail.trim();
      if (fromField) return true;
      const c = contributors?.find((x) => x.id === contributorChoice.actor.id);
      return Boolean(c?.email?.trim() || c?.invitedEmail?.trim());
    }
    return Boolean(inviteeEmail.trim());
  }, [contributorChoice, selectedRole, needsEventCrewEmail, inviteeEmail, contributors, rolesStillAssignable]);

  const submitAdd = async () => {
    if (!contributorChoice || !canSubmitAdd) return;
    setIsSettling(true);
    try {
      const ok =
        contributorChoice.kind === 'existing'
          ? await addExisting(contributorChoice.actor)
          : await createNew(contributorChoice.name);
      if (ok) {
        setContributorChoice(null);
        setSelectedRole('');
        setInviteeEmail('');
      }
    } finally {
      setIsSettling(false);
    }
  };

  const contributorSummaryName =
    contributorChoice?.kind === 'existing'
      ? contributorChoice.actor.name
      : contributorChoice?.kind === 'new'
        ? contributorChoice.name
        : '';

  const isPending = useMemo(
    () =>
      isAddContributorPending ||
      isCreateContributorPending ||
      isCreateContributorRolePending ||
      isCreateRoleSlotPending ||
      isSettling,
    [
      isAddContributorPending,
      isCreateContributorPending,
      isCreateContributorRolePending,
      isCreateRoleSlotPending,
      isSettling,
    ],
  );

  if (selectableRoles.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5} sx={{ mb: 1 }}>
      {!contributorChoice ? (
        <ContributorAutocomplete
          key="pick-contributor"
          options={allContributorActors}
          loading={isPending}
          labelKey="event.detail.crewWidget.addContributorLabel"
          autocompleteId="add-crew-contributor-autocomplete"
          disabled={isPending}
          onSelectExisting={(actor) => setContributorChoice({ kind: 'existing', actor })}
          onCreateNew={(name) => setContributorChoice({ kind: 'new', name })}
        />
      ) : (
        <>
          {isPending ? (
            <ContributorBusyNameField
              label={t('common.name')}
              name={contributorSummaryName}
              testId="add-crew-contributor-busy"
            />
          ) : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={contributorSummaryName}>
                  {contributorSummaryName}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  disabled={isPending}
                  onClick={() => setContributorChoice(null)}
                  sx={{ flexShrink: 0 }}
                >
                  {t('event.detail.crewWidget.changeContributor')}
                </Button>
              </Stack>
              <FormControl variant="standard" fullWidth>
                <InputLabel id="add-crew-role-label">{t('event.detail.crewWidget.selectRole')}</InputLabel>
                <Select
                  labelId="add-crew-role-label"
                  value={rolesStillAssignable.includes(selectedRole) ? selectedRole : ''}
                  label={t('event.detail.crewWidget.selectRole')}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={isPending || rolesStillAssignable.length === 0}
                >
                  {rolesStillAssignable.map((role) => (
                    <MenuItem key={role} value={role}>
                      {t(`common.roleLabels.${role}` as 'common.roleLabels.owner')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {contributorChoice && rolesStillAssignable.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t('event.detail.crewWidget.allRolesAssigned')}
                </Typography>
              ) : null}
              {selectedRole && needsEventCrewEmail ? (
                <TextField
                  variant="standard"
                  fullWidth
                  type="email"
                  label={t('event.detail.crewWidget.inviteeEmailLabel')}
                  value={inviteeEmail}
                  onChange={(e) => setInviteeEmail(e.target.value)}
                  disabled={isPending}
                  autoComplete="email"
                />
              ) : null}
              {selectedRole && rolesStillAssignable.includes(selectedRole) ? (
                <Button
                  variant="contained"
                  size="small"
                  loading={isPending}
                  disabled={isPending || !canSubmitAdd}
                  onClick={() => void submitAdd()}
                >
                  {t('event.detail.crewWidget.confirmAddCrew')}
                </Button>
              ) : null}
            </>
          )}
        </>
      )}
    </Stack>
  );
};
